import { ref, computed } from 'vue';
import bondService from '@/service/bondService';
import { getTodayForAPI } from '@/utils/dateUtil';

export function useKtbBonds() {
  // State
  const ktbBonds = ref([]);
  const bondTypes = ref([]); // 동적으로 로드될 타입들
  const isLoading = ref(false);

  // Computed
  const maturityRangeTypes = computed(() => {
    const types = {};
    bondTypes.value.forEach(bondType => {
      types[bondType.type] = bondType.title;
    });
    return types;
  });

  const typeOrder = computed(() => {
    return bondTypes.value.map(bondType => bondType.type);
  });

  const groupedBonds = computed(() => {
    const grouped = {};

    // 모든 타입에 대해 빈 배열로 초기화
    typeOrder.value.forEach(type => {
      grouped[type] = [];
    });

    // 채권들을 타입별로 분류
    ktbBonds.value.forEach(bond => {
      const type = bond.maturityRangeType || 'UNCATEGORIZED';
      if (grouped[type]) {
        grouped[type].push(bond);
      } else {
        // 예상치 못한 타입이 있다면 UNCATEGORIZED에 추가
        if (!grouped['UNCATEGORIZED']) {
          grouped['UNCATEGORIZED'] = [];
        }
        grouped['UNCATEGORIZED'].push(bond);
      }
    });

    return grouped;
  });

  // Methods
  const loadBondTypes = async () => {
    try {
      const response = await bondService.getKtbBondTypes();
      bondTypes.value = response || [];
    } catch (error) {
      console.error('국고채 타입 로드 실패:', error);
      // 에러 시 기본값 사용
      bondTypes.value = [];
    }
  };

  const loadKtbBonds = async (baseDate) => {
    try {
      isLoading.value = true;

      // 타입과 채권 데이터를 병렬로 로드
      await Promise.all([loadBondTypes(), loadBondData(baseDate)]);
    } catch (error) {
      console.error('국고채 데이터 로드 실패:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const loadBondData = async baseDate => {
    if (!baseDate) {
      baseDate = getTodayForAPI();
    }

    const response = await bondService.getKtbBonds(baseDate);

    if (Array.isArray(response)) {
      ktbBonds.value = response;
    } else {
      console.error('응답이 배열이 아닙니다:', response);
      ktbBonds.value = [];
    }
  };

  const createKtbBond = async (bondData, baseDate = null) => {
    try {
      isLoading.value = true;
      if (!baseDate) {
        baseDate = getTodayForAPI();
      }

      const newBond = await bondService.createKtbBond({
        baseDate,
        code: bondData.code,
        bondName: bondData.bondName,
        yield: bondData.yield,
        isBenchmark: bondData.isBenchmark,
        maturityDate: bondData.maturityDate,
        maturityRangeType: bondData.maturityRangeType,
      });

      ktbBonds.value.push(newBond);
      return newBond;
    } catch (error) {
      console.error('국고채 생성 실패:', error);
      throw new Error('국고채 생성 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const updateKtbBond = async (bond, updates, baseDate = null) => {
    try {
      isLoading.value = true;
      if (!baseDate) {
        baseDate = getTodayForAPI();
      }

      await bondService.updateKtbBond({
        baseDate,
        code: bond.code,
        bondName: bond.bondName,
        yield: updates.yield ?? bond.yield,
        isBenchmark: updates.isBenchmark ?? bond.isBenchmark,
        maturityRangeType: updates.maturityRangeType ?? bond.maturityRangeType,
      });

      // 로컬 데이터 업데이트
      const bondIndex = ktbBonds.value.findIndex(b => b.code === bond.code);
      if (bondIndex !== -1) {
        ktbBonds.value[bondIndex] = {
          ...ktbBonds.value[bondIndex],
          ...updates,
        };
      }
    } catch (error) {
      console.error('국고채 수정 실패:', error);
      throw new Error('국고채 수정 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const toggleBenchmark = async (bond, baseDate = null) => {
    try {
      await updateKtbBond(bond, {
        isBenchmark: !bond.isBenchmark,
      }, baseDate);
    } catch (error) {
      console.error('지표채권 토글 실패:', error);
      throw error;
    }
  };

  const updateMaturityRangeType = async (bond, newType, baseDate = null) => {
    try {
      await updateKtbBond(bond, {
        maturityRangeType: newType,
      }, baseDate);
    } catch (error) {
      console.error('만기 유형 변경 실패:', error);
      throw error;
    }
  };

  return {
    // State
    ktbBonds,
    bondTypes,
    isLoading,

    // Computed
    maturityRangeTypes,
    typeOrder,
    groupedBonds,

    // Methods
    loadBondTypes,
    loadKtbBonds,
    createKtbBond,
    updateKtbBond,
    toggleBenchmark,
    updateMaturityRangeType,
  };
}
